/**
 * Pure model for Square Stitcher.
 *
 * The player may rotate unstitched squares freely, but a stitch move exists
 * only when the square orientation matches its seam target. Free diagonal
 * welding is not generated.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import levelsData from "../data/square-stitcher-levels.json" with { type: "json" };

export interface SquareStitcherSquare {
  readonly id: string;
  readonly label: string;
  readonly sdp: string;
  readonly phase: string;
  readonly row: number;
  readonly col: number;
  readonly rotation: number;
  readonly targetRotation: number;
  readonly value: number;
}

export interface SquareStitcherLevel {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  readonly targetStitches: number;
  readonly squares: readonly SquareStitcherSquare[];
}

export type SquareStitcherStatus = "playing" | "cleared";

export interface SquareStitcherState {
  readonly levelId: string;
  readonly levelName: string;
  readonly brief: string;
  readonly rotations: Readonly<Record<string, number>>;
  readonly stitchedSquareIds: readonly string[];
  readonly value: number;
  readonly turn: number;
  readonly status: SquareStitcherStatus;
  readonly lastEvent: string;
}

export type SquareStitcherMove =
  | {
      readonly type: "stitch";
      readonly squareId: string;
      readonly label: string;
      readonly value: number;
    }
  | {
      readonly type: "rotate";
      readonly squareId: string;
      readonly label: string;
    };

interface SquareStitcherLevelsData {
  readonly levels: readonly SquareStitcherLevel[];
}

export const SQUARE_STITCHER_LEVELS = (levelsData as SquareStitcherLevelsData)
  .levels;
export const DEFAULT_SQUARE_STITCHER_LEVEL_ID =
  SQUARE_STITCHER_LEVELS[0]?.id ?? "";

const LEVEL_BY_ID = new Map(
  SQUARE_STITCHER_LEVELS.map((level) => [level.id, level]),
);

export function getSquareStitcherLevel(levelId: string): SquareStitcherLevel {
  const level = LEVEL_BY_ID.get(levelId);
  if (!level) throw new Error(`Unknown Square Stitcher level: ${levelId}`);
  return level;
}

export function createSquareStitcherState(
  levelId = DEFAULT_SQUARE_STITCHER_LEVEL_ID,
): SquareStitcherState {
  const level = getSquareStitcherLevel(levelId);
  return {
    levelId: level.id,
    levelName: level.name,
    brief: level.brief,
    rotations: Object.fromEntries(
      level.squares.map((square) => [
        square.id,
        normalizeRotation(square.rotation),
      ]),
    ),
    stitchedSquareIds: [],
    value: 0,
    turn: 0,
    status: "playing",
    lastEvent:
      "Rotate a square until its arrow faces the target seam, then stitch it.",
  };
}

export function squareAligned(
  state: SquareStitcherState,
  square: SquareStitcherSquare,
): boolean {
  return (
    currentRotation(state, square.id) ===
    normalizeRotation(square.targetRotation)
  );
}

export function legalSquareStitcherMoves(
  state: SquareStitcherState,
): readonly SquareStitcherMove[] {
  if (state.status !== "playing") return [];
  const stitched = new Set(state.stitchedSquareIds);
  const unstitched = getSquareStitcherLevel(state.levelId).squares.filter(
    (square) => !stitched.has(square.id),
  );
  const stitchMoves = unstitched
    .filter((square) => squareAligned(state, square))
    .map((square) => ({
      type: "stitch" as const,
      squareId: square.id,
      label: square.label,
      value: square.value,
    }));
  const rotateMoves = unstitched.map((square) => ({
    type: "rotate" as const,
    squareId: square.id,
    label: square.label,
  }));
  return [...stitchMoves, ...rotateMoves];
}

export function freeDiagonalWeldGenerated(): boolean {
  return false;
}

export function stepSquareStitcher(
  state: SquareStitcherState,
  move: SquareStitcherMove,
): SquareStitcherState {
  const legal = legalSquareStitcherMoves(state).some(
    (candidate) =>
      candidate.type === move.type && candidate.squareId === move.squareId,
  );
  if (!legal) return state;

  if (move.type === "rotate") {
    const nextRotation = normalizeRotation(
      currentRotation(state, move.squareId) + 1,
    );
    return {
      ...state,
      rotations: { ...state.rotations, [move.squareId]: nextRotation },
      turn: state.turn + 1,
      lastEvent: `${move.label} rotated to face ${rotationName(nextRotation)}.`,
    };
  }

  const stitchedSquareIds = [...state.stitchedSquareIds, move.squareId];
  const level = getSquareStitcherLevel(state.levelId);
  const next: SquareStitcherState = {
    ...state,
    stitchedSquareIds,
    value: state.value + move.value,
    turn: state.turn + 1,
    lastEvent: `${move.label} stitched into the 2-cell grid with a matching seam orientation.`,
  };

  if (stitchedSquareIds.length >= level.targetStitches) {
    return {
      ...next,
      status: "cleared",
      lastEvent:
        "Grid stitched. Every square matched its seam orientation; free diagonal welding never appeared.",
    };
  }

  return next;
}

export function squareStitcherProgress(state: SquareStitcherState): number {
  const target = getSquareStitcherLevel(state.levelId).targetStitches;
  if (target <= 0) return 100;
  return Math.round((state.stitchedSquareIds.length / target) * 100);
}

export function currentRotation(
  state: SquareStitcherState,
  squareId: string,
): number {
  return normalizeRotation(state.rotations[squareId] ?? 0);
}

export function rotationName(rotation: number): string {
  return (
    ["north", "east", "south", "west"][normalizeRotation(rotation)] ?? "north"
  );
}

function normalizeRotation(rotation: number): number {
  return ((rotation % 4) + 4) % 4;
}
